import React from 'react';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import LazyLoad from 'react-lazyload';

import { Helpers } from '../../core/src/helpers';
import ImageWithPlaceholder from '../../shared/components/image-with-placeholder';
import { getProdHideStatus } from '../../product/components/product-list';
import ProductShowPlaceholder from '../../product/components/product-show-placeholder';
import GiftGuideCard from './gift-guide-product-tile';
import ProductDetailModal from '../../product/components/product-detail-modal';
import AddToCart from './add-to-cart';
import FilteredProductList from './filtered-product-list';
import FooterBanner from './footer-banner';
import { GRIDLAYOUT } from './utils';

import '../css/gift-guide.less';

const HeroBanner = loadable(() => Helpers.retryFunc(() => import('../../hero-banner/hero-banner')));

class GiftGuide extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            productModal: false,
        };

        this.createCustomProductCard = this.createCustomProductCard.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.openProductDetailModal = this.openProductDetailModal.bind(this);
        this.prefetchInterval = null;
    }

    createCustomProductCard(product, key, enableAppPDP, className, productIndex, placeholder) {
        //pass in placehodler that has price component so that prices are available earlier and can filter OOS/NO IR
        if (placeholder) {
            return <ProductShowPlaceholder product={product} className="gift-guide-product-card" />;
        }
        return GiftGuideCard({
            product,
            onAdd: () => {
                this.openProductDetailModal(product);
            },
            key,
        });
    }

    closeModal() {
        this.setState({ productModal: false });
    }

    componentDidMount() {
        const { pg, productGroup = {}, fetchProducts, prefetchTabs } = this.props;
        if (!productGroup.products || productGroup.products.length === 0) {
            fetchProducts(pg);
        } else if (prefetchTabs) {
            this.prefetchProduct();
        }
    }
    componentDidUpdate(prevProps) {
        if (
            prevProps.pg !== this.props.pg &&
            prevProps.pg !== this.props.pg &&
            (!this.props.productGroup || !this.props.productGroup.key || !this.props.productGroup.productList)
        ) {
            prevProps.fetchProducts(this.props.pg);
        } else if (this.props.prefetchTabs) {
            this.prefetchProduct();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.prefetchInterval);
    }

    prefetchProduct = () => {
        const { pg, prefetchProducts } = this.props;
        const [vanityUrl] = pg.split(':');
        //if there are still keys left to fetch, prefetch them
        if (prefetchProducts) {
            this.prefetchInterval = setTimeout(() => prefetchProducts(vanityUrl), 400);
        }
    };

    prdFilterCondition = (prd, params) => {
        const { autoHideType, disableAutoHide } = this.props;
        return getProdHideStatus(autoHideType, disableAutoHide, prd, params);
    };

    customBannerClick = e => {
        const sku = e.target.getAttribute('data-sku');
        if (sku) {
            e.preventDefault();
            const product = this.props.productGroup.products.find(p => p.sku === sku);
            if (product) {
                this.setState({
                    productModal: { product },
                });
            }
        }
    };

    getFeaturedBanner = Helpers.memoize(
        productGroup => {
            try {
                let featuredProduct =
                    productGroup && productGroup.products
                        ? productGroup.products.find(product => product.metadata.featured === true)
                        : null;
                let { featuredContent } = (featuredProduct && featuredProduct.metadata) || {};
                let featuredProductBanner =
                    featuredProduct && featuredProduct.active && featuredContent
                        ? {
                              product: featuredProduct,
                              title: featuredContent.title,
                              cta: featuredContent.cta,
                              ctaLink: featuredContent.ctaLink,
                              backgroundImage: featuredContent.image,
                              backgroundImageMobile: featuredContent.imageMobile,
                              description: featuredContent.description,
                          }
                        : null;
                return featuredProductBanner;
            } catch (e) {}
            return null;
        },
        productGroup => `${productGroup.key}`,
    );

    render() {
        let {
            pg,
            slug,
            enableAppPDP,
            productGroup,
            match,
            leftMenu,
            sortGtmActions,
            ToggleFilter,
            disableSearch,
            tabProps,
            enableGiftGuideVisIdTile,
            sortedProducts,
            productTileVariation,
            gridColumns,
        } = this.props;
        let featuredProductBanner = this.getFeaturedBanner(productGroup);
        const jumpInfo = {
            pg: pg.split(':').pop(),
            slugKey: match && match.params && match.params.slug,
        };
        const { productGroupHero, productGroupCSS, footerBanner = [] } = productGroup.metaData || {};
        const products = sortedProducts || productGroup.products || [];

        return (
            <div className={'gift-guide' + `${gridColumns === GRIDLAYOUT.classic ? ' og' : ' visId'}`}>
                {productGroupHero ? (
                    <HeroBanner
                        onClick={this.customBannerClick}
                        className="pg-custom-banner"
                        customHtml={productGroupHero}
                        productsList={products}
                    />
                ) : null}
                {productGroupCSS ? <style dangerouslySetInnerHTML={Helpers.createMarkup(productGroupCSS)} /> : null}
                {featuredProductBanner ? (
                    <HeroBanner banner={featuredProductBanner} className="featured-product-banner">
                        <ImageWithPlaceholder
                            className="hero-image"
                            src={featuredProductBanner.backgroundImageMobile || featuredProductBanner.backgroundImage}
                        />
                        <div className="text-container">
                            <h1 className="featured-item-title">{featuredProductBanner.title}</h1>
                            <p className="featured-item-description">{featuredProductBanner.description}</p>
                            <div className="featured-cart">
                                <AddToCart
                                    openModal={() => this.openProductDetailModal(featuredProductBanner.product)}
                                    text={featuredProductBanner.cta}
                                    ctaLink={featuredProductBanner.ctaLink}
                                />
                            </div>
                        </div>
                    </HeroBanner>
                ) : null}
                <div className="top-header">
                    <div className="top-header-text-wrapper">
                        <h2>{productGroup.name}</h2>
                        <p className="top-header-description">{productGroup.description}</p>
                    </div>
                </div>
                <FilteredProductList
                    onAdd={this.openProductDetailModal}
                    products={products}
                    enableAppPDP={enableAppPDP}
                    prdFilterCondition={this.prdFilterCondition}
                    leftMenu={leftMenu}
                    sortGtmActions={sortGtmActions}
                    ToggleFilter={ToggleFilter}
                    disableSearch={disableSearch}
                    tabProps={tabProps}
                    pg={pg}
                    enableGiftGuideVisIdTile={enableGiftGuideVisIdTile}
                    productTileVariation={productTileVariation}
                    gridColumns={gridColumns}
                />
                <ProductDetailModal
                    isOpen={this.state.productModal !== false}
                    product={this.state.productModal.product}
                    disableXsellPopup={true}
                    closeModal={this.closeModal}
                    enableAppPDP={enableAppPDP}
                    jumpInfo={jumpInfo}
                />
                {footerBanner.map((footer, i) => (
                    <LazyLoad once={true} offset={100} key={`footer-banner-${i}`}>
                        <FooterBanner
                            key={`gift-guide-footer-${i}`}
                            customBannerClick={this.customBannerClick}
                            footer={footer}
                        />
                    </LazyLoad>
                ))}
            </div>
        );
    }

    openProductDetailModal(product) {
        const { history, match } = this.props;
        const {
            params: { basename },
        } = match;
        this.setState({
            productModal: { product },
        });
    }
}

GiftGuide.defaultProps = {
    productGroup: {
        products: [],
    },
};

export default withRouter(GiftGuide);
